import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  CircularProgress,
  FormControl,
  Typography,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { Controller } from "react-hook-form";
import axios from "axios";
import { getDocumentByFileStoreId } from "@egovernments/digit-ui-libraries/src/utils/fileType";

const UniversalFileUploader = ({
  name = "fileInput",
  label = "Upload File",
  maxFiles = 5,
  uploadedDocs = [],
  setUploadedDocs = () => { },
  control,
  required = false,
  showError = false,
  tenantId = "up",
  module = "common-upload",
}) => {
  const fileInputRef = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [showToast, setShowToast] = useState(null);

  // ✅ File formats allowed
  const allowedExtensions = [
    ".pdf",
    ".png",
    ".jpg",
    ".jpeg",
    ".dxf",
    ".mp3",
    ".mp4",
    ".mov",
    ".avi",
    ".mkv",
  ];

  const maxSize = 300 * 1024 * 1024; // 300 MB

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    setIsLoading(true);
    setShowToast(null);

    const alreadyUploaded = uploadedDocs?.length || 0;
    const filesToUpload = files.slice(0, maxFiles - alreadyUploaded);

    for (let file of filesToUpload) {
      const ext = "." + file.name.split(".").pop().toLowerCase();

      if (!allowedExtensions.includes(ext)) {
        setShowToast({ error: true, label: `Invalid format: ${file.name}` });
        continue;
      }

      if (file.size > maxSize) {
        setShowToast({
          error: true,
          label: `${file.name} exceeds 300 MB limit.`,
        });
        continue;
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("tenantId", tenantId);
      formData.append("module", module);
      formData.append("tag", "universal-upload");

      try {
        const resp = await axios.post("/filestore/v1/files", formData, {});
        const filesId = resp.data.files[0];

        setUploadedDocs((prev) => [
          ...prev,
          {
            documentType: name,
            fileStoreId: filesId.fileStoreId,
            additionalDetails: { fileName: file.name, title: label },
          },
        ]);
      } catch (err) {
        console.error(err.message);
        setShowToast({
          error: true,
          label: `Upload failed for ${file.name}`,
        });
      }
    }

    setIsLoading(false);
    e.target.value = null;
  };

  const handleDelete = (fileStoreId) => {
    setUploadedDocs((prev) =>
      prev.filter((doc) => doc.fileStoreId !== fileStoreId)
    );
  };

  const docsForThisType = uploadedDocs?.filter(
    (doc) => doc.documentType === name
  );

  const getAcceptType = () =>
    allowedExtensions.join(",");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {label} {required && <span style={{ color: "red" }}>*</span>}
      </Typography>

      <FormControl fullWidth>
        <Button
          variant="contained"
          onClick={() => fileInputRef.current.click()}
          sx={{
            height: "32px",
            mb: 1,
            fontSize: "0.75rem",
            backgroundColor: "rgb(21,124,179)",
            "&:hover": { backgroundColor: "#004e6c" },
          }}
          disabled={docsForThisType.length >= maxFiles}
        >
          {isLoading ? (
            <CircularProgress sx={{ color: "#fff" }} size={20} />
          ) : (
            <React.Fragment>
              <CloudUploadOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
              Upload File
            </React.Fragment>
          )}
        </Button>

        {control ? (
          <Controller
            name={name}
            control={control}
            rules={{ required: required && docsForThisType.length === 0 }}
            render={() => (
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={getAcceptType()}
                style={{ display: "none" }}
                onChange={handleFileUpload}
              />
            )}
          />
        ) : (
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={getAcceptType()}
            style={{ display: "none" }}
            onChange={handleFileUpload}
          />
        )}
      </FormControl>

      {/* Uploaded Files */}
      {docsForThisType.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {docsForThisType.map((doc) => (
            <Box
              key={doc.fileStoreId}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "0.8rem",
                p: "4px 8px",
                mb: "6px",
                borderRadius: "4px",
                backgroundColor: "#f1f1f1",
              }}
            >
              {doc.additionalDetails?.fileName || "Uploaded File"}
              <Box>
                <IconButton
                  size="small"
                  onClick={() =>
                    getDocumentByFileStoreId(doc.fileStoreId, setIsPreviewLoading)
                  }
                >
                  {isPreviewLoading ? (
                    <CircularProgress size={14} />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(doc.fileStoreId)}
                >
                  <DeleteForeverIcon color="error" fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Error Messages */}
      {required && showError && docsForThisType.length === 0 && (
        <Typography color="error" variant="caption">
          {`${label} is required.`}
        </Typography>
      )}
      {!!showToast?.error && (
        <Typography color="error" variant="caption">
          {showToast.label}
        </Typography>
      )}
    </Box>
  );
};

export default UniversalFileUploader;
